<script setup lang="ts">
const { currentPalette } = useTheme();

const attrs = useAttrs();

const emit = defineEmits<{
  zoom: [];
}>();

const props = withDefaults(
  defineProps<{
    autoplay?: boolean | "true" | "false";
    controls?: boolean | "true" | "false";
    height?: string | number;
    loop?: boolean | "true" | "false";
    muted?: boolean | "true" | "false";
    poster?: string;
    preload?: "auto" | "metadata" | "none";
    src?: string;
    width?: string | number;

    customControls?: boolean;
    zoomable?: boolean;
  }>(),
  {}
);

const wrapper = templateRef<HTMLDivElement>("wrapper");
const player = templateRef<HTMLVideoElement>("player");

const mouseMoved = refAutoReset(false, 3000);

const playing = ref(false);
const volume = ref(1);
const playbackRate = ref(1);
const currentTime = ref(0);
const duration = ref(1);
const buffered = shallowRef<TimeRanges>();

const hideControlsBeforePlayed = ref(true);
whenever(playing, () => (hideControlsBeforePlayed.value = false));

onMounted(() => {
  playing.value = player.value.autoplay;
  volume.value = player.value.volume;
  playbackRate.value = player.value.playbackRate;
  currentTime.value = player.value.currentTime;
  duration.value = player.value.duration;
  buffered.value = player.value.buffered;

  player.value.addEventListener("play", () => (playing.value = true));

  player.value.addEventListener("pause", () => (playing.value = false));

  player.value.addEventListener(
    "volumechange",
    () => (volume.value = player.value.volume)
  );

  player.value.addEventListener(
    "ratechange",
    () => (playbackRate.value = player.value.playbackRate)
  );

  player.value.addEventListener(
    "timeupdate",
    () => (currentTime.value = player.value.currentTime)
  );

  player.value.addEventListener("ended", () => (playing.value = false));

  player.value.addEventListener("loadedmetadata", () => {
    duration.value = player.value.duration;
  });

  player.value.addEventListener("progress", () => {
    buffered.value = player.value.buffered;
  });
});

const play = () => player.value.play();
const pause = () => player.value.pause();
const togglePlay = () => (player.value.paused ? play() : pause());
const seek = (time: number) => (player.value.currentTime = time);
const setVolume = (volume: number) => (player.value.volume = volume);
const setPlaybackRate = (rate: number) => (player.value.playbackRate = rate);
const setMuted = (muted: boolean) => (player.value.muted = muted);
const setLoop = (loop: boolean) => (player.value.loop = loop);
const setCurrentTime = (time: number) => (player.value.currentTime = time);

const zoom = () => {
  pause();
  emit("zoom");
};

const slotData = {
  props,
  attrs,
  player,
  playing,
  volume,
  playbackRate,
  currentTime,
  play,
  pause,
  togglePlay,
  seek,
  setVolume,
  setPlaybackRate,
  setMuted,
  setLoop,
  setCurrentTime,
};

defineExpose({
  player,

  play,
  pause,
  togglePlay,
  seek,
  setVolume,
  setPlaybackRate,
  setMuted,
  setLoop,
  setCurrentTime,
});
</script>

<template>
  <div
    class="video-player relative"
    ref="wrapper"
    :class="{ 'cursor-none': !mouseMoved }"
    @mousemove="mouseMoved = true"
  >
    <video
      ref="player"
      :autoplay="props.autoplay"
      :controls="props.controls"
      :height="props.height"
      :loop="props.loop"
      :muted="props.muted"
      :poster="props.poster"
      :preload="props.preload"
      :src="props.src"
      :width="props.width"
      class="video-player__video"
      v-bind="attrs"
    >
      <slot v-bind="slotData"> </slot>
    </video>

    <button
      v-if="props.customControls"
      v-show="!playing"
      :class="[
        'video-player__button-play z-20',
        'z-10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
        'cursor-pointer',
        'text-grey-500',
      ]"
      @click="play"
      :title="playing ? 'Pauza' : 'Odtwórz'"
    >
      <svg
        width="233"
        height="165"
        viewBox="0 0 233 165"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="block w-[14.534375rem] h-auto"
      >
        <path
          opacity="0.6"
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M32.0811 0.645386C14.4079 0.645386 0.0810547 14.9723 0.0810547 32.6454V132.355C0.0810547 150.028 14.4079 164.355 32.0811 164.355H200.633C218.306 164.355 232.633 150.028 232.633 132.355V0.645386H32.0811ZM85.0649 120.596L151.05 82.4999L85.0649 44.4035V120.596Z"
          fill="currentColor"
        />
      </svg>
    </button>

    <div
      v-if="props.customControls"
      v-show="mouseMoved && !hideControlsBeforePlayed"
      class="video-player__controls absolute inset-0 z-10"
    >
      <div
        class="video-player__controls-row video-player__controls-row--bottom absolute bottom-5 inset-x-5 flex items-center justify-between gap-5"
      >
        <slot name="controls-bottom" v-bind="slotData">
          <div
            class="video-player__controls video-player__controls--start flex items-center justify-start gap-5"
          >
            <slot name="controls-bottom-start" v-bind="slotData">
              <VideoPlayerButton
                vertical="bottom"
                horizontal="start"
                @click="togglePlay"
                :title="playing ? 'Pauza' : 'Odtwórz'"
              >
                <Icon
                  :name="playing ? 'ns:pause' : 'ns:play'"
                  class="block w-[1.125rem] h-[1.125rem]"
                />
              </VideoPlayerButton>
            </slot>
          </div>

          <div
            class="video-player__controls video-player__controls--center grow flex items-center justify-center gap-5"
          >
            <slot name="controls-bottom-center" v-bind="slotData">
              <VideoPlayerProgress
                class="grow"
                :model-value="currentTime"
                @update:model-value="(value) => setCurrentTime(value)"
                :duration="duration"
                :buffered="buffered"
              />
            </slot>
          </div>

          <div
            class="video-player__controls video-player__controls--end flex items-center justify-end gap-5"
          >
            <slot name="controls-bottom-end" v-bind="slotData">
              <VideoPlayerButton
                v-if="props.zoomable"
                vertical="bottom"
                horizontal="end"
                @click="zoom"
                title="Powiększ"
              >
                <Icon name="ns:maximize-2" class="w-[1.125rem] h-[1.125rem]" />
              </VideoPlayerButton>
            </slot>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>
